// src/components/Register.jsx

import React, { useEffect, useState } from "react";
import Swal from "sweetalert2";
import "./Register.css";
import { API_BASE_NOSLASH as API_BASE } from "../apiConfig";

export default function Register() {
  const emptyForm = {
    id: "",
    name: "",
    username: "",
    password: "",
    phone: "",
    email: "",
    address: "",
    role: "Doctor",
    speciality: "",
    profile_picture: "",
  };

  const [formData, setFormData] = useState(emptyForm);

  const [profileFile, setProfileFile] = useState(null);
  const [preview, setPreview] = useState("");

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);

  const [search, setSearch] = useState("");

  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  /* =========================================================
     LOAD USERS
  ========================================================= */

  useEffect(() => {
    loadUsers();
  }, []);

  /* =========================================================
     SEARCH
  ========================================================= */

  useEffect(() => {
    const q = search.trim().toLowerCase();

    if (!q) {
      setFilteredUsers(users);
      return;
    }

    const filtered = users.filter((user) => {
      return (
        (user.name || "").toLowerCase().includes(q) ||
        (user.username || "").toLowerCase().includes(q) ||
        (user.phone || "").toLowerCase().includes(q) ||
        (user.email || "").toLowerCase().includes(q) ||
        (user.role || "").toLowerCase().includes(q) ||
        (user.speciality || "").toLowerCase().includes(q)
      );
    });

    setFilteredUsers(filtered);
  }, [search, users]);

  /* =========================================================
     LOAD USER LIST
  ========================================================= */

  const loadUsers = async () => {
    setListLoading(true);

    try {
      const response = await fetch(`${API_BASE}/get_users.php`);

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        console.error("Invalid JSON:", text);

        Swal.fire(
          "Error",
          "User API did not return valid JSON.",
          "error"
        );

        return;
      }

      if (result.status === "success") {
        const data = Array.isArray(result.data) ? result.data : [];

        setUsers(data);
        setFilteredUsers(data);
      } else {
        Swal.fire(
          "Error",
          result.message || "Unable to load users.",
          "error"
        );
      }
    } catch (error) {
      console.error(error);

      Swal.fire(
        "Error",
        "Unable to connect to user API.",
        "error"
      );
    } finally {
      setListLoading(false);
    }
  };

  /* =========================================================
     INPUT CHANGE
  ========================================================= */

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "phone") {
      const clean = value.replace(/\D/g, "").slice(0, 10);

      setFormData((prev) => ({
        ...prev,
        phone: clean,
      }));

      return;
    }

    if (name === "role") {
      setFormData((prev) => ({
        ...prev,
        role: value,
        speciality: value === "Doctor" ? prev.speciality : "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  /* =========================================================
     PROFILE PICTURE
  ========================================================= */

  const handleProfileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const allowedTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/webp",
    ];

    if (!allowedTypes.includes(file.type)) {
      Swal.fire(
        "Invalid Image",
        "Only JPG, JPEG, PNG and WEBP files are allowed.",
        "warning"
      );

      e.target.value = "";
      return;
    }

    if (file.size > 3 * 1024 * 1024) {
      Swal.fire(
        "Image Too Large",
        "Profile picture must be less than 3 MB.",
        "warning"
      );

      e.target.value = "";
      return;
    }

    setProfileFile(file);

    const objectUrl = URL.createObjectURL(file);
    setPreview(objectUrl);
  };

  const removeSelectedProfile = () => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setProfileFile(null);

    if (editingId && formData.profile_picture) {
      setPreview(
        `${API_BASE}/uploads/profile/${formData.profile_picture}`
      );
    } else {
      setPreview("");
    }

    const input = document.getElementById("profile_picture");

    if (input) {
      input.value = "";
    }
  };

  /* =========================================================
     PASSWORD VALIDATION
  ========================================================= */

  const validatePassword = (password) => {
    const regex =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])[A-Za-z\d!@#$%^&*]{8,}$/;

    return regex.test(password);
  };

  /* =========================================================
     FORM VALIDATION
  ========================================================= */

  const validateForm = () => {
    if (!formData.name.trim()) {
      Swal.fire("Required", "Please enter the name.", "warning");
      return false;
    }

    if (!formData.username.trim()) {
      Swal.fire(
        "Required",
        "Please enter the username.",
        "warning"
      );
      return false;
    }

    if (!editingId && !formData.password) {
      Swal.fire(
        "Required",
        "Please enter a password.",
        "warning"
      );
      return false;
    }

    if (formData.password) {
      if (!validatePassword(formData.password)) {
        Swal.fire(
          "Weak Password",
          "Password must have minimum 8 characters, uppercase, lowercase, number and special character.",
          "warning"
        );

        return false;
      }
    }

    if (!formData.phone) {
      Swal.fire(
        "Required",
        "Please enter phone number.",
        "warning"
      );

      return false;
    }

    if (formData.phone.length !== 10) {
      Swal.fire(
        "Invalid Phone",
        "Phone number must contain exactly 10 digits.",
        "warning"
      );

      return false;
    }

    if (!formData.email.trim()) {
      Swal.fire(
        "Required",
        "Please enter email address.",
        "warning"
      );

      return false;
    }

    const emailRegex =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email.trim())) {
      Swal.fire(
        "Invalid Email",
        "Please enter a valid email address.",
        "warning"
      );

      return false;
    }

    if (!formData.address.trim()) {
      Swal.fire(
        "Required",
        "Please enter contact address.",
        "warning"
      );

      return false;
    }

    if (!formData.role) {
      Swal.fire(
        "Required",
        "Please select user role.",
        "warning"
      );

      return false;
    }

    if (formData.role === "Doctor" && !formData.speciality) {
      Swal.fire(
        "Required",
        "Please select doctor speciality.",
        "warning"
      );
      return false;
    }

    return true;
  };

  /* =========================================================
     SAVE / UPDATE
  ========================================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);

    try {
      const data = new FormData();

      data.append("name", formData.name.trim());
      data.append("username", formData.username.trim());
      data.append("password", formData.password);
      data.append("phone", formData.phone);
      data.append("email", formData.email.trim());
      data.append("address", formData.address.trim());
      data.append("role", formData.role);
      data.append(
        "speciality",
        formData.role === "Doctor" ? formData.speciality : ""
      );

      if (profileFile) {
        data.append("profile_picture", profileFile);
      }

      let endpoint = `${API_BASE}/register.php`;

      if (editingId) {
        endpoint = `${API_BASE}/update_user.php`;

        data.append("id", editingId);
      }

      const response = await fetch(endpoint, {
        method: "POST",
        body: data,
      });

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch (error) {
        console.error("Invalid API response:", text);

        Swal.fire(
          "Server Error",
          "PHP API did not return valid JSON.",
          "error"
        );

        return;
      }

      if (result.status === "success") {
        Swal.fire({
          icon: "success",
          title: editingId
            ? "User Updated"
            : "User Registered",
          text: result.message,
          timer: 1800,
          showConfirmButton: false,
        });

        resetForm();
        loadUsers();
      } else {
        Swal.fire(
          "Unable to Save",
          result.message || "Operation failed.",
          "error"
        );
      }
    } catch (error) {
      console.error(error);

      Swal.fire(
        "Server Error",
        "Unable to communicate with server.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     EDIT USER
  ========================================================= */

  const handleEdit = (user) => {
    setEditingId(user.id);

    setFormData({
      id: user.id || "",
      name: user.name || "",
      username: user.username || "",
      password: "",
      phone: user.phone || "",
      email: user.email || "",
      address: user.address || "",
      role: user.role || "Doctor",
      speciality: user.role === "Doctor" ? user.speciality || "" : "",
      profile_picture: user.profile_picture || "",
    });

    setProfileFile(null);

    if (user.profile_picture) {
      setPreview(
        `${API_BASE}/uploads/profile/${user.profile_picture}`
      );
    } else {
      setPreview("");
    }

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  /* =========================================================
     DEACTIVATE
  ========================================================= */

  const handleDeactivate = async (user) => {
    const confirm = await Swal.fire({
      title: "Deactivate User?",
      html: `
        <div style="text-align:center;">
          <strong>${user.name}</strong><br/>
          ${user.username}<br/>
          <small>${user.role}</small>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Deactivate",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE}/deactivate_user.php`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id: user.id,
          }),
        }
      );

      const text = await response.text();

      let result;

      try {
        result = JSON.parse(text);
      } catch {
        console.error(text);

        Swal.fire(
          "Error",
          "Deactivate API did not return valid JSON.",
          "error"
        );

        return;
      }

      if (result.status === "success") {
        Swal.fire(
          "Deactivated",
          result.message,
          "success"
        );

        if (
          editingId &&
          String(editingId) === String(user.id)
        ) {
          resetForm();
        }

        loadUsers();
      } else {
        Swal.fire(
          "Error",
          result.message || "Unable to deactivate user.",
          "error"
        );
      }
    } catch (error) {
      console.error(error);

      Swal.fire(
        "Error",
        "Unable to connect to server.",
        "error"
      );
    }
  };

  /* =========================================================
     RESET
  ========================================================= */

  const resetForm = () => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setFormData(emptyForm);

    setProfileFile(null);
    setPreview("");
    setEditingId(null);

    const input = document.getElementById("profile_picture");

    if (input) {
      input.value = "";
    }
  };

  /* =========================================================
     RENDER
  ========================================================= */

  return (
    <div className="user-management-page">

      {/* ====================================================
          HEADER
      ==================================================== */}

      <div className="user-management-header">
        <div>
          <h2>User Management</h2>

          <p>
            Register doctors and other hospital staff and
            manage their access.
          </p>
        </div>

        <div className="user-count-box">
          <span>{users.length}</span>
          <small>Users</small>
        </div>
      </div>

      <div className="user-management-grid">

        {/* ==================================================
            LEFT - FORM
        ================================================== */}

        <div className="user-form-card">

          <div className="card-title-row">
            <div>
              <h3>
                {editingId
                  ? "Edit User"
                  : "Register User"}
              </h3>

              <p>
                {editingId
                  ? "Update staff account information."
                  : "Create a new staff login account."}
              </p>
            </div>
          </div>

          <form
            onSubmit={handleSubmit}
            encType="multipart/form-data"
          >

            {/* PROFILE */}

            <div className="profile-upload-section">

              <div className="profile-preview">

                {preview ? (
                  <img
                    src={preview}
                    alt="Profile Preview"
                  />
                ) : (
                  <div className="profile-placeholder">
                    <i className="fa fa-user"></i>
                  </div>
                )}

              </div>

              <div className="profile-upload-controls">
                <label htmlFor="profile_picture">
                  Profile Picture
                </label>

                <input
                  id="profile_picture"
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp"
                  onChange={handleProfileChange}
                />

                <small>
                  JPG, PNG or WEBP. Maximum 3 MB.
                </small>

                {preview && (
                  <button
                    type="button"
                    className="remove-photo-btn"
                    onClick={removeSelectedProfile}
                  >
                    <i className="fa fa-times"></i>
                    Remove selected image
                  </button>
                )}

              </div>

            </div>

            {/* NAME */}

            <div className="form-group">
              <label>
                Name
                <span>*</span>
              </label>

              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="Doctor / Staff Name"
              />
            </div>

            {/* USERNAME */}

            <div className="form-group">
              <label>
                Username
                <span>*</span>
              </label>

              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleChange}
                placeholder="Login username"
                autoComplete="off"
              />
            </div>

            {/* PASSWORD */}

            <div className="form-group">
              <label>
                Password
                {!editingId && <span>*</span>}
              </label>

              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder={
                  editingId
                    ? "Leave blank to keep current password"
                    : "Enter strong password"
                }
                autoComplete="new-password"
              />

              <small className="field-help">
                Minimum 8 characters with uppercase,
                lowercase, number and special character.
              </small>
            </div>

            {/* PHONE + EMAIL */}

            <div className="form-two-columns">

              <div className="form-group">
                <label>
                  Phone
                  <span>*</span>
                </label>

                <input
                  type="text"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="10 digit number"
                  maxLength="10"
                />
              </div>

              <div className="form-group">
                <label>
                  Email
                  <span>*</span>
                </label>

                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email address"
                />
              </div>

            </div>

            {/* ROLE */}

            <div className="form-group">
              <label>
                Role
                <span>*</span>
              </label>

              <select
                name="role"
                value={formData.role}
                onChange={handleChange}
              >
                <option value="Doctor">
                  Doctor
                </option>

                <option value="Reception">
                  Reception
                </option>

                <option value="Nursing Station">
                  Nursing Station
                </option>

              </select>
            </div>

            {/* SPECIALITY - DOCTOR ONLY */}

            {formData.role === "Doctor" && (
              <div className="form-group">
                <label>
                  Speciality
                  <span>*</span>
                </label>

                <select
                  name="speciality"
                  value={formData.speciality}
                  onChange={handleChange}
                >
                  <option value="">Select Speciality</option>
                  <option value="Dental">Dental</option>
                  <option value="Pediatric">Pediatric</option>
                  <option value="General">General</option>
                </select>
              </div>
            )}

            {/* ADDRESS */}

            <div className="form-group">
              <label>
                Contact Address
                <span>*</span>
              </label>

              <textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Complete contact address"
                rows="4"
              />
            </div>

            {/* BUTTONS */}

            <div className="form-action-buttons">

              <button
                type="submit"
                className="save-user-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <i className="fa fa-spinner fa-spin"></i>
                    Saving...
                  </>
                ) : editingId ? (
                  <>
                    <i className="fa fa-save"></i>
                    Update User
                  </>
                ) : (
                  <>
                    <i className="fa fa-user-plus"></i>
                    Register User
                  </>
                )}
              </button>

              {editingId && (
                <button
                  type="button"
                  className="cancel-edit-btn"
                  onClick={resetForm}
                >
                  Cancel Edit
                </button>
              )}

            </div>

          </form>

        </div>

        {/* ==================================================
            RIGHT - LIST
        ================================================== */}

        <div className="user-list-card">

          <div className="user-list-header">

            <div>
              <h3>Registered Users</h3>
              <p>
                Manage doctors, reception and nursing
                station users.
              </p>
            </div>

          </div>

          {/* SEARCH */}

          <div className="user-search-box">
            <i className="fa fa-search"></i>

            <input
              type="text"
              placeholder="Search name, username, phone, email, role or speciality..."
              value={search}
              onChange={(e) =>
                setSearch(e.target.value)
              }
            />

            {search && (
              <button
                type="button"
                onClick={() => setSearch("")}
              >
                ×
              </button>
            )}
          </div>

          {/* TABLE */}

          <div className="user-table-wrapper">

            <table className="user-table">

              <thead>
                <tr>
                  <th>Sl No</th>
                  <th>User</th>
                  <th>Contact</th>
                  <th>Role</th>
                  <th>Speciality</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>

              <tbody>

                {listLoading ? (

                  <tr>
                    <td
                      colSpan="7"
                      className="table-message"
                    >
                      Loading users...
                    </td>
                  </tr>

                ) : filteredUsers.length === 0 ? (

                  <tr>
                    <td
                      colSpan="7"
                      className="table-message"
                    >
                      No users found.
                    </td>
                  </tr>

                ) : (

                  filteredUsers.map((user, index) => (

                    <tr key={user.id}>

                      <td>
                        {index + 1}
                      </td>

                      <td>

                        <div className="user-profile-cell">

                          {user.profile_picture ? (

                            <img
                              src={`${API_BASE}/uploads/profile/${user.profile_picture}`}
                              alt={user.name}
                            />

                          ) : (

                            <div className="table-user-icon">
                              <i className="fa fa-user"></i>
                            </div>

                          )}

                          <div>
                            <strong>
                              {user.name}
                            </strong>

                            <small>
                              @{user.username}
                            </small>
                          </div>

                        </div>

                      </td>

                      <td>

                        <div className="contact-cell">

                          <span>
                            <i className="fa fa-phone"></i>
                            {user.phone || "-"}
                          </span>

                          <span>
                            <i className="fa fa-envelope-o"></i>
                            {user.email || "-"}
                          </span>

                        </div>

                      </td>

                      <td>

                        <span
                          className={`role-badge role-${(
                            user.role || ""
                          )
                            .toLowerCase()
                            .replace(/\s+/g, "-")}`}
                        >
                          {user.role}
                        </span>

                      </td>

                      <td>
                        {user.role === "Doctor"
                          ? user.speciality || "-"
                          : "-"}
                      </td>

                      <td>

                        <span
                          className={`status-badge ${
                            user.status === "active"
                              ? "status-active"
                              : "status-inactive"
                          }`}
                        >
                          {user.status === "active"
                            ? "Active"
                            : "Inactive"}
                        </span>

                      </td>

                      <td>

                        <div className="table-actions">

                          <button
                            type="button"
                            className="edit-user-btn"
                            title="Edit"
                            onClick={() =>
                              handleEdit(user)
                            }
                          >
                            <i className="fa fa-pencil"></i>
                          </button>

                          {user.status === "active" && (

                            <button
                              type="button"
                              className="deactivate-user-btn"
                              title="Deactivate"
                              onClick={() =>
                                handleDeactivate(user)
                              }
                            >
                              <i className="fa fa-ban"></i>
                            </button>

                          )}

                        </div>

                      </td>

                    </tr>

                  ))
                )}

              </tbody>

            </table>

          </div>

        </div>

      </div>

    </div>
  );
}